import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import {
  DEFAULT_PROGRESS,
  normalizeUserProgress,
  checkOnboardingComplete,
} from '@/logic/progressNormalize';
import { setSoundEnabled, setHapticEnabled } from '@/utils/sound';
import { setMusicEnabled } from '@/utils/musicCoach';
import { identify } from '@/utils/analytics';

const AppContext = createContext(null);

/** Race a promise against a timeout. Returns `fallback` if `ms` elapses. */
function withTimeout(promise, ms, fallback) {
  let timer;
  return Promise.race([
    Promise.resolve(promise).catch(() => fallback),
    new Promise((resolve) => { timer = setTimeout(() => resolve(fallback), ms); }),
  ]).finally(() => clearTimeout(timer));
}

export function AppProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try {
      const saved = localStorage.getItem('bb7');
      if (saved) {
        const data = JSON.parse(saved);
        return data.lang || 'en';
      }
    } catch {}
    return 'en';
  });

  const [user, setUser] = useState(null);
  const [userProgress, setUserProgress] = useState(DEFAULT_PROGRESS);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // profiles row snapshot ({ is_active, plan, subscription_id }). null
  // until the first fetch lands; treated as "allowed through" while
  // loading so a slow Supabase query cannot strand a paying user on
  // the paywall. AppShell only renders MembershipGate when this is
  // explicitly { is_active: false }.
  const [profile, setProfile] = useState(null);
  // Supabase fires PASSWORD_RECOVERY on the click-through from a
  // reset-password email. AuthScreen watches this flag to swap into
  // the "choose new password" mode instead of the normal login form.
  // Cleared by AuthScreen once the user finishes the flow (or by
  // signOut, which cascades into session = null below).
  const [passwordRecovery, setPasswordRecovery] = useState(false);

  const fetchProgress = useCallback(async (userId) => {
    if (!supabase) return;
    try {
      // 3s race — same rationale as the getUser() timeout above.
      const result = await withTimeout(
        supabase
          .from('user_progress')
          .select('data')
          .eq('id', userId)
          .maybeSingle(),
        3000,
        null
      );

      const data = result?.data ?? null;
      const fetchError = result?.error ?? null;
      if (fetchError) throw fetchError;

      if (data?.data) {
        const d = data.data;
        setOnboardingComplete(checkOnboardingComplete(d));

        // Field-mapping lives in src/logic/progressNormalize.js so the
        // legacy-vs-React schema coercion can be unit-tested without
        // rendering the provider. See tests/progressNormalize.test.js.
        const nextProgress = normalizeUserProgress(d);

        if (nextProgress) setUserProgress(nextProgress);
        if (d.lang) setLang(d.lang);
      }
      setError(null);
    } catch (err) {
      // Soft-fail: AppShell treats null/missing progress as "allowed
      // through" and we hydrate from localStorage below. Logging at
      // warn keeps real errors (pageerror, throw) prominent in
      // Sentry/PostHog without burying them under flaky-network noise.
      console.warn('Failed to fetch progress:', err);
      setError(err.message);
      // Check localStorage fallback for onboarding state
      try {
        const saved = localStorage.getItem('bb7');
        if (saved) {
          const d = JSON.parse(saved);
          setOnboardingComplete(checkOnboardingComplete(d));
        }
      } catch {}
    }
  }, []);

  // Fetch the profiles row (is_active + plan). Runs alongside
  // fetchProgress on auth change so the membership gate decision is
  // available by the time AppShell renders. Soft-failing: any error
  // or timeout leaves profile=null, which AppShell treats as
  // "allowed through" — a brief network hiccup should never lock
  // out a paying user.
  const fetchProfile = useCallback(async (userId) => {
    if (!supabase) return;
    try {
      const result = await withTimeout(
        supabase
          .from('profiles')
          .select('is_active, plan, subscription_id')
          .eq('id', userId)
          .maybeSingle(),
        3000,
        null,
      );
      const row = result?.data ?? null;
      const fetchError = result?.error ?? null;
      if (fetchError) {
        console.warn('Failed to fetch profile:', fetchError);
        setProfile(null);
        return;
      }
      setProfile(row || null);
    } catch (err) {
      console.warn('Profile fetch threw:', err);
      setProfile(null);
    }
  }, []);

  const refreshProgress = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    await Promise.all([fetchProgress(user.id), fetchProfile(user.id)]);
    setLoading(false);
  }, [user, fetchProgress, fetchProfile]);

  // Refetch just the profiles row. Used by MembershipGate when the
  // user returns from a Stripe checkout tab — we don't want to
  // trigger the full loading overlay (which would blank the paywall)
  // just to see if is_active flipped.
  const refreshProfile = useCallback(async () => {
    if (!user) return;
    await fetchProfile(user.id);
  }, [user, fetchProfile]);

  // Optimistic in-memory patch of userProgress. Use this when a gated
  // onboarding screen has just written a flag (e.g. language_selected,
  // consent_complete) to Supabase via safeWrite and needs the AppShell
  // gate to advance on the same tick — without waiting for the
  // round-trip refetch in refreshProgress. The Supabase write is still
  // persisted for cross-device continuity; this only updates the
  // in-memory source of truth so the UI can progress even if the
  // write is slow, hits RLS, or races the refetch.
  const patchProgress = useCallback((partial) => {
    if (!partial) return;
    setUserProgress((prev) => ({ ...prev, ...partial }));
  }, []);

  // Mirror userProgress.sound_enabled to the runtime Web Audio gate so
  // playSessionCompleteChime / playConfirmDing become no-ops the instant
  // the user flips the Profile toggle. No event emission needed —
  // effects re-run whenever the field changes.
  useEffect(() => {
    setSoundEnabled(!!userProgress?.sound_enabled);
  }, [userProgress?.sound_enabled]);

  // Bridge auth → analytics. No-op until a real provider is wired
  // up at boot (see src/utils/initAnalytics.js); when one is, the
  // user's Supabase UUID flows through. Identify is intentionally
  // sparse — no email, no traits — so the analytics tier never
  // becomes a backdoor for PII. Sign-out is also a no-op since
  // PostHog will keep the last-known id until reset() is called.
  useEffect(() => {
    if (user?.id) identify(user.id);
  }, [user?.id]);

  // Same pattern for haptics. Defaults to true when undefined (silent
  // confirmation by design), false only when the user explicitly
  // disables it from Profile.
  useEffect(() => {
    setHapticEnabled(userProgress?.haptic_enabled !== false);
  }, [userProgress?.haptic_enabled]);

  // Background music defaults ON — opt-out, since the looping track
  // is one of the things that makes the app feel like a guided
  // session instead of a worksheet. Users who want silence flip it
  // off in Profile (mirrors haptic_enabled's "explicit false" rule).
  useEffect(() => {
    setMusicEnabled(userProgress?.music_enabled !== false);
  }, [userProgress?.music_enabled]);

  // Mirror userProgress.theme to <html data-theme="..."> so the CSS
  // overrides in src/css/variables.css recolor the entire app the
  // instant the user picks a palette in Profile. main.jsx applies the
  // saved theme synchronously before React renders, so this effect
  // only catches subsequent changes (Supabase fetch resolving with a
  // different theme, or the Profile picker firing patchProgress).
  // Wraps the change in a short .bb-theme-transitioning class so
  // colour surfaces crossfade rather than snap.
  const isFirstThemeApply = useRef(true);
  useEffect(() => {
    const theme = userProgress?.theme;
    const root = document.documentElement;
    // Apply the theme attribute first so the dataset.theme change and
    // the transition class land in the same paint.
    if (theme === 'sage' || theme === 'twilight') {
      root.dataset.theme = theme;
    } else {
      // 'rose' (default) or any unknown value: clear the attribute so
      // the :root rule wins instead of an unmatched [data-theme=...].
      delete root.dataset.theme;
    }
    // Sync the <meta name="theme-color"> to the active palette's --bg
    // so the iOS status-bar tint and Android browser chrome match the
    // theme. Apple's signature unified-surface feel — the OS chrome
    // visually merges with the app rather than fighting it.
    requestAnimationFrame(() => {
      const bg = getComputedStyle(root).getPropertyValue('--bg').trim();
      if (bg) {
        let meta = document.querySelector('meta[name="theme-color"]');
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute('name', 'theme-color');
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', bg);
      }
    });
    // First apply (boot): no transition class — main.jsx already set
    // the theme synchronously and we don't want to animate from rose
    // to itself for users who never changed.
    if (isFirstThemeApply.current) {
      isFirstThemeApply.current = false;
      return undefined;
    }
    root.classList.add('bb-theme-transitioning');
    const timer = setTimeout(() => {
      root.classList.remove('bb-theme-transitioning');
    }, 280);
    // Cleanup on rapid theme toggles: cancel the pending removal AND
    // strip the class so the next effect run starts clean.
    return () => {
      clearTimeout(timer);
      root.classList.remove('bb-theme-transitioning');
    };
  }, [userProgress?.theme]);

  // Mirror userProgress.evening_mode to <html data-evening-mode="true">.
  // Kept in its own effect so it composes cleanly with theme changes —
  // the CSS block in variables.css sits at the end of the file and
  // overrides whichever palette is active.
  useEffect(() => {
    const root = document.documentElement;
    if (userProgress?.evening_mode) {
      root.dataset.eveningMode = 'true';
    } else {
      delete root.dataset.eveningMode;
    }
  }, [userProgress?.evening_mode]);

  useEffect(() => {
    let mounted = true;

    async function init() {
      if (!supabase) {
        // No Supabase client — fall back to localStorage only
        try {
          const saved = localStorage.getItem('bb7');
          if (saved) {
            const d = JSON.parse(saved);
            setOnboardingComplete(checkOnboardingComplete(d));
            if (d.lang) setLang(d.lang);
          }
        } catch {}
        if (mounted) setLoading(false);
        return;
      }

      try {
        // 3s race so a stalled getUser() (hung token refresh, flaky
        // network) cannot strand the user on the LoadingScreen logo.
        // On timeout we treat the user as unauthenticated and fall
        // through to localStorage, same as the vanilla-side fix in
        // src/main.js (commit 4125c04).
        const authResult = await withTimeout(
          supabase.auth.getUser(), 3000, null
        );
        const authUser = authResult?.data?.user ?? null;
        if (!mounted) return;

        if (authUser) {
          setUser(authUser);
          await Promise.all([fetchProgress(authUser.id), fetchProfile(authUser.id)]);
        } else {
          try {
            const saved = localStorage.getItem('bb7');
            if (saved) {
              const d = JSON.parse(saved);
              setOnboardingComplete(checkOnboardingComplete(d));
              if (d.lang) setLang(d.lang);
            }
          } catch {}
        }
      } catch (err) {
        if (mounted) setError(err.message);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    init();

    if (!supabase) return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // PASSWORD_RECOVERY fires on the click-through from a Supabase
        // reset-password email. The event only sets the recovery flag
        // — it does NOT gate the rest of the app's auth flow. We
        // deliberately ignore the event when the caller is a fully
        // signed-in user (e.g. clicking a stale link from a second
        // device) so the normal session stays usable; AuthScreen
        // checks the flag in combination with a live session to
        // decide whether to render the "set new password" form.
        if (event === 'PASSWORD_RECOVERY') {
          setPasswordRecovery(true);
        }
        if (session?.user) {
          setUser(session.user);
          await Promise.all([
            fetchProgress(session.user.id),
            fetchProfile(session.user.id),
          ]);
        } else {
          setUser(null);
          setUserProgress(DEFAULT_PROGRESS);
          setOnboardingComplete(false);
          setPasswordRecovery(false);
          setProfile(null);
        }
      },
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProgress, fetchProfile]);

  const value = {
    lang,
    setLang,
    user,
    userProgress,
    onboardingComplete,
    loading,
    error,
    refreshProgress,
    refreshProfile,
    patchProgress,
    passwordRecovery,
    setPasswordRecovery,
    profile,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}

export default AppContext;
